/**
 * Every module in one approved revision, grouped by what happened to it.
 *
 * @module components/portfolio/comparison/ModuleInventory
 */

import React from "react";

import type { ModuleDifference } from "@/components/portfolio/comparison/diff-types";
import { InventoryPanel } from "@/components/portfolio/comparison/InventoryPanel";
import { recordedCreditsLabel } from "@/components/shared/credits";
import { Badge, MutedText } from "@/components/ui";
import type { Module, Programme } from "@/types";

export interface ModuleInventoryProps {
  /** The revision this side belongs to. */
  label: string;
  programme: Programme;
  differences: readonly ModuleDifference[];
  /** Which revision this is, since removals belong to one side and additions the other. */
  side: "before" | "after";
}

/** The chip a module carries, or nothing when it survived the revision untouched. */
function moduleStatus(
  difference: ModuleDifference | undefined,
  side: ModuleInventoryProps["side"],
): { label: string; tone: "success" | "error" | "warning" } | undefined {
  if (side === "before" && difference?.statuses.includes("removed")) {
    return { label: "Removed", tone: "error" };
  }
  if (side === "after" && difference?.statuses.includes("new")) {
    return { label: "New", tone: "success" };
  }
  if (difference && !difference.statuses.includes("unchanged")) {
    return { label: "Changed", tone: "warning" };
  }
  return undefined;
}

/**
 * @example
 * ```tsx
 * <ModuleInventory label={revision.institutionalLabel} programme={revision.data} differences={modules} side="before" />
 * ```
 */
export function ModuleInventory({
  label,
  programme,
  differences,
  side,
}: ModuleInventoryProps): React.ReactElement {
  const differenceById = new Map(differences.map((difference) => [difference.id, difference]));
  const modules = [...(programme.modules ?? [])].sort(
    (left, right) => left.code.localeCompare(right.code) || left.title.localeCompare(right.title),
  );
  // Every group is defined by the chip its rows wear, so a module cannot be
  // filed under one heading and labelled as another, and none can fall
  // between the three and vanish.
  const status = (module: Module): ReturnType<typeof moduleStatus> =>
    moduleStatus(differenceById.get(module.id), side);
  const groups = [
    {
      label: "Changed modules",
      items: modules.filter((module) => status(module)?.label === "Changed"),
    },
    {
      label: side === "before" ? "Removed in later version" : "New additions",
      items: modules.filter((module) => {
        const chip = status(module)?.label;
        return chip === "Removed" || chip === "New";
      }),
    },
    {
      label: "Unchanged modules",
      items: modules.filter((module) => status(module) === undefined),
    },
  ];

  return (
    <InventoryPanel<Module, (typeof groups)[number]>
      label={label}
      name="module list"
      noun={{ one: "module", many: "modules" }}
      groups={groups}
      itemKey={(module) => module.id}
      emptyMessage="No modules recorded."
      renderItem={(module) => {
        const chip = status(module);
        return (
          <>
            <div className="min-w-0">
              <div className="font-medium">{module.title}</div>
              <MutedText size="caption" className="mt-1">
                {`${module.code} · ${recordedCreditsLabel(module.credits)}`}
              </MutedText>
            </div>
            {chip ? (
              <Badge size="sm" tone={chip.tone}>
                {chip.label}
              </Badge>
            ) : null}
          </>
        );
      }}
    />
  );
}
